import axios from 'axios';

// const API_BASE_URL = 'http://127.0.0.1:8000';
// const API_BASE_URL = 'http://192.168.101.2:8000';
const API_BASE_URL = process.env.REACT_APP_API_URL;
console.log('API_BASE_URL:', API_BASE_URL); // Debug line

// Validate API_BASE_URL
if (!API_BASE_URL) {
    console.error('❌ REACT_APP_API_URL is not set!');
} else {
    console.log('✅ API_BASE_URL is set to:', API_BASE_URL);
}

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 120000  // Default 2 minutes timeout for all requests (can be overridden per request)
});

// Debug: Log the actual axios instance configuration
console.log('Axios instance baseURL:', api.defaults.baseURL);
console.log('Axios instance config:', {
    baseURL: api.defaults.baseURL,
    headers: api.defaults.headers,
    withCredentials: api.defaults.withCredentials
});

// Add authentication interceptor
api.interceptors.request.use((config) => {
    // Debug: Log every request
    console.log('🚀 Making request to:', config.baseURL + config.url);
    console.log('Request config:', {
        method: config.method,
        baseURL: config.baseURL,
        url: config.url,
        fullURL: config.baseURL + config.url
    });
    
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle authentication errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// User Management Service
const userService = {
    // Get all users (admin only)
    getUsers: async () => {
        const response = await api.get('/auth/users');
        return response.data;
    },

    // Create a new user (admin only)
    createUser: async (userData) => {
        const response = await api.post('/auth/users', userData);
        return response.data;
    },

    // Update a user (admin only)
    updateUser: async (userId, userData) => {
        const response = await api.put(`/auth/users/${userId}`, userData);
        return response.data;
    },

    // Delete a user (admin only)
    deleteUser: async (userId) => {
        const response = await api.delete(`/auth/users/${userId}`);
        return response.data;
    },

    // Validate password strength
    validatePassword: async (password) => {
        const response = await api.get(`/auth/validate-password?password=${encodeURIComponent(password)}`);
        return response.data;
    },

    // Validate username format
    validateUsername: async (username) => {
        const response = await api.get(`/auth/validate-username?username=${encodeURIComponent(username)}`);
        return response.data;
    },

    // Get current user info
    getCurrentUser: async () => {
        const response = await api.get('/auth/me');
        return response.data;
    }
};

const firewallService = {
    // Firewall Credentials
    createFirewall: async (firewallData) => {
        const response = await api.post('/firewalls/', firewallData);
        return response.data;
    },

    listFirewalls: async () => {
        const response = await api.get('/firewalls/');
        return response.data;
    },

    getFirewall: async (id) => {
        const response = await api.get(`/firewalls/${id}`);
        return response.data;
    },

    updateFirewall: async (id, firewallData) => {
        const response = await api.put(`/firewalls/${id}`, firewallData);
        return response.data;
    },

    deleteFirewall: async (id) => {
        const response = await api.delete(`/firewalls/${id}`);
        return response.data;
    },

    // Audit Operations
        getAuditHistory: async (filters = {}, page = 1, pageSize = 20) => {
        try {
            const params = new URLSearchParams();
            if (filters.startDate) params.append('start_date', filters.startDate);
            if (filters.endDate) params.append('end_date', filters.endDate);
            if (filters.benchmarkType) params.append('benchmark_type', filters.benchmarkType);
            if (filters.status) params.append('status', filters.status);
            if (filters.reportStatus) params.append('report_status', filters.reportStatus);
            if (filters.aiModel) params.append('ai_model', filters.aiModel);
            
            // SOC Alert filter — translate to score range for server-side filtering
            if (filters.socAlert) {
                const alertRanges = {
                    'Critical Alert': { min: 0, max: 29.99 },
                    'High Alert': { min: 30, max: 49.99 },
                    'Medium Alert': { min: 50, max: 79.99 },
                    'Low Alert': { min: 80, max: 95.99 },
                    'Compliant': { min: 96, max: 100 },
                };
                const range = alertRanges[filters.socAlert];
                if (range) {
                    params.append('min_score', range.min);
                    params.append('max_score', range.max);
                }
            }

            // Advanced filters (manual score range overrides socAlert if both set)
            if (filters.minScore) params.append('min_score', filters.minScore);
            if (filters.maxScore) params.append('max_score', filters.maxScore);
            if (filters.minDuration) params.append('min_duration', filters.minDuration);
            if (filters.maxDuration) params.append('max_duration', filters.maxDuration);
            if (filters.minObs) params.append('min_obs', filters.minObs);
            if (filters.maxObs) params.append('max_obs', filters.maxObs);
            if (filters.obsCategory) params.append('obs_category', filters.obsCategory);

            params.append('page', page);
            params.append('page_size', pageSize);
            
            const response = await api.get(`/audit-history?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching audit history:', error);
            return { audits: [], pagination: { total_count: 0, total_pages: 0 } };
        }
    },

    getResearchMetrics: async () => {
        try {
            const response = await api.get('/dashboard/research-metrics');
            return response.data;
        } catch (error) {
            console.error('Error fetching research metrics:', error);
            return null;
        }
    },

    getLatestAudit: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/latest-audit`);
            return response.data;
        } catch (error) {
            console.error('Error in getLatestAudit:', error);
            throw error;
        }
    },

    getLatestAuditResults: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/latest-audit-results`);
            return response.data;
        } catch (error) {
            console.error('Error in getLatestAuditResults:', error);
            throw error;
        }
    },

    fetchConfig: async (firewallId) => {
        try {
            const response = await api.post(`/firewalls/${firewallId}/fetch-config`);
            if (!response.data || !response.data.config) {
                throw new Error('Invalid response from server');
            }
            return response.data;
        } catch (error) {
            console.error('Error in fetchConfig:', error);
            throw error;
        }
    },

    getConfig: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/config`);
            if (!response.data || !response.data.config) {
                throw new Error('Invalid response from server');
            }
            return response.data;
        } catch (error) {
            console.error('Error in getConfig:', error);
            throw error;
        }
    },

    getStoredConfig: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/stored-config`);
            if (!response.data || !response.data.config) {
                throw new Error('Invalid response from server');
            }
            return response.data;
        } catch (error) {
            console.error('Error in getStoredConfig:', error);
            throw error;
        }
    },

    downloadStoredConfig: async (firewallId) => {
        try {
            // Use fetch directly like the working download implementations
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/firewalls/${firewallId}/stored-config/download`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            return blob;
        } catch (error) {
            console.error('Error downloading stored configuration:', error);
            throw error;
        }
    },

    stopAudit: async (firewallId) => {
        const response = await api.post(`/firewalls/${firewallId}/stop-audit`);
        return response.data;
    },

    getAuditProgress: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/audit-progress`);
            return response.data;
        } catch (error) {
            console.error('Error in getAuditProgress:', error);
            throw error;
        }
    },

    getAuditResults: async (firewallId, auditId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/audit-history/${auditId}/results`);
            return response.data;
        } catch (error) {
            console.error('Error in getAuditResults:', error);
            throw error;
        }
    },

    deleteAudit: async (firewallId, auditId) => {
        try {
            const response = await api.delete(`/firewalls/${firewallId}/audit-history/${auditId}`);
            return response.data;
        } catch (error) {
            console.error('Error in deleteAudit:', error);
            throw error;
        }
    },

    // Audit Reports
    getAuditReports: async (firewallId, page = 1, pageSize = 10, { polling = false } = {}) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/audit-reports`, {
                params: { page, page_size: pageSize },
                timeout: polling ? 30000 : 15000  // 30s when polling during audit (backend may be busy), 15s otherwise
            });
            return response.data;
        } catch (error) {
            if (polling && error.code === 'ECONNABORTED') {
                // During polling, timeouts are expected when backend is busy with audit — return null silently
                return null;
            }
            console.error('Error in getAuditReports:', error);
            throw error;
        }
    },

    downloadAuditReport: async (reportId) => {
        try {
            const response = await api.get(`/audit-reports/${reportId}/download`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('Error in downloadAuditReport:', error);
            throw error;
        }
    },

    deleteAuditReport: async (reportId) => {
        try {
            const response = await api.delete(`/audit-reports/${reportId}`);
            return response.data;
        } catch (error) {
            console.error('Error in deleteAuditReport:', error);
            throw error;
        }
    },

    // AI Model Information
    getAIModelsForFirewall: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/ai-models`);
            return response.data;
        } catch (error) {
            console.error('Error in getAIModelsForFirewall:', error);
            throw error;
        }
    },

    getAIModelsSummary: async () => {
        try {
            const response = await api.get('/ai-models/summary');
            return response.data;
        } catch (error) {
            console.error('Error in getAIModelsSummary:', error);
            throw error;
        }
    },

    // Dedicated configuration fetch endpoints
    fetchFortinetConfig: async (firewallId) => {
        try {
            const response = await api.post(`/firewalls/${firewallId}/fetch-fortinet-config`);
            return response.data;
        } catch (error) {
            console.error('Error in fetchFortinetConfig:', error);
            throw error;
        }
    },

    fetchCiscoFtdConfig: async (firewallId) => {
        try {
            const response = await api.post(`/firewalls/${firewallId}/fetch-cisco-ftd-config`);
            return response.data;
        } catch (error) {
            console.error('Error in fetchCiscoFtdConfig:', error);
            throw error;
        }
    },

    fetchPaloAltoConfig: async (firewallId) => {
        try {
            const response = await api.post(`/firewalls/${firewallId}/fetch-paloalto-config`);
            return response.data;
        } catch (error) {
            console.error('Error in fetchPaloAltoConfig:', error);
            throw error;
        }
    },

    fetchCheckpointConfig: async (firewallId) => {
        try {
            const response = await api.post(`/firewalls/${firewallId}/fetch-checkpoint-config`);
            return response.data;
        } catch (error) {
            console.error('Error in fetchCheckpointConfig:', error);
            throw error;
        }
    },

    // Get available sections for Direct Audit
    getAnalyzeConfigSections: async (firewallId) => {
        try {
            const response = await api.get(`/firewalls/${firewallId}/analyze-config-sections`);
            return response.data;
        } catch (error) {
            console.error('Error in getAnalyzeConfigSections:', error);
            throw error;
        }
    },

    // Direct configuration analysis without benchmark or RAG
    analyzeConfigDirect: async (firewallId, analysisData) => {
        try {
            console.log('🔍 analyzeConfigDirect called with:', {
                firewallId,
                analysisData,
                url: `/firewalls/${firewallId}/analyze-config`,
                fullUrl: `${API_BASE_URL}/firewalls/${firewallId}/analyze-config`
            });
            
            // Use 10 minutes timeout for all Direct Audit requests
            const timeout = 600000; // 10 minutes (600 seconds)
            
            console.log(`⏱️ Using timeout: ${timeout / 1000 / 60} minutes for Direct Audit`);
            
            const response = await api.post(`/firewalls/${firewallId}/analyze-config`, analysisData, {
                timeout: timeout
            });
            console.log('✅ analyzeConfigDirect response:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Error in analyzeConfigDirect:', error);
            console.error('❌ Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                url: error.config?.url,
                baseURL: error.config?.baseURL,
                code: error.code
            });
            
            // If timeout occurred, provide helpful message
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                // Create a custom error with helpful message
                const timeoutError = new Error('Request timed out. The audit may still be processing in the background. Please check the Reports tab in a few minutes.');
                timeoutError.code = 'ECONNABORTED';
                timeoutError.isTimeout = true;
                throw timeoutError;
            }
            
            throw error;
        }
    },

    // Local (Ollama / MLX) model: whether it is the only local model loaded.
    getLocalModelStatus: async (model) => {
        const response = await api.get('/local-models/status', { params: { model } });
        return response.data;
    },

    // Unload every other local model (Ollama and LM Studio) and load this one.
    // source: 'button' (Load model) or 'at_run' (the step before Run Analysis).
    // A large model can take minutes; the backend gives up after 280 s.
    loadLocalModel: async (model, source) => {
        const response = await api.post('/local-models/load', { model, source }, { timeout: 300000 });
        return response.data;
    },
};

// Timeout utility for Ollama API calls
const withTimeout = (promise, timeoutMs = 5000, errorMessage = 'Request timeout') => {
    return Promise.race([
        promise,
        new Promise((_, reject) => 
            setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
        )
    ]);
};

// Ollama Models Management
const ollamaService = {
    getModels: async () => {
        try {
            const response = await withTimeout(
                api.get('/ollama/models'),
                2000,
                'Ollama unavailable'
            );
            return response.data;
        } catch (error) {
            // Ollama being unreachable is normal when not deployed — return empty silently
            return { models: [] };
        }
    },
    // Direct query to Ollama (no RAG)
    ask: async (prompt, model, sessionId, mode, chatSessionId = null) => {
        try {
            const requestData = {
                prompt,
                model,
                session_id: sessionId,
                mode: mode
            };
            
            // Add chat_session_id if provided (for maintaining conversation context from database)
            if (chatSessionId) {
                requestData.chat_session_id = chatSessionId;
            }
            
            // Use longer timeout for AI chat requests (120 seconds) to allow for model loading and processing
            const response = await withTimeout(
                api.post('/ollama/ask', requestData),
                120000, // 120 seconds (2 minutes) - allows time for model loading and complex responses
                'Ollama service is not responding. Please check if Ollama is running and accessible.'
            );
            return response.data;
        } catch (error) {
            console.error('Error querying Ollama directly:', error);
            // Check if it's a timeout error
            if (error.message && (error.message.includes('timeout') || error.message.includes('not responding'))) {
                // Provide more helpful error message for timeout
                const isTimeout = error.message.includes('timeout') || error.code === 'ECONNABORTED';
                if (isTimeout) {
                    throw new Error('The request timed out. This may happen on the first message while the model is loading. Please try again in a moment.');
                }
                throw new Error('Ollama service is not responding. Please check if Ollama is running and accessible.');
            }
            throw error;
        }
    },
};

// OpenAI Models Management
const openaiService = {
    getModels: async () => {
        try {
            const response = await api.get('/openai/models');
            return response.data;
        } catch (error) {
            console.error('Error fetching OpenAI models:', error);
            throw error;
        }
    },
};

// Free Online AI Models (Google AI Studio)
const googleAiStudioService = {
    getModels: async () => {
        try {
            const response = await api.get('/googleaistudio/models');
            return response.data;
        } catch (error) {
            console.error('Error fetching Google AI Studio models:', error);
            throw error;
        }
    },
};

// Free Online AI Models (OpenRouter)
const openRouterService = {
    getModels: async () => {
        try {
            const response = await api.get('/openrouter/models');
            return response.data;
        } catch (error) {
            console.error('Error fetching OpenRouter models:', error);
            throw error;
        }
    },
};

// Groq (Fast inference for Llama, Mixtral, etc.)
const groqService = {
    getModels: async () => {
        try {
            const response = await api.get('/groq/models');
            return response.data;
        } catch (error) {
            console.error('Error fetching Groq models:', error);
            throw error;
        }
    },
};

// MLX models served by LM Studio. Like Ollama, an unreachable server is
// normal (the Mac may be asleep), so failure returns an empty list quietly.
const mlxService = {
    getModels: async () => {
        try {
            const response = await withTimeout(api.get('/mlx/models'), 8000, 'MLX unavailable');
            return response.data;
        } catch (error) {
            return { models: [] };
        }
    },
};

const claudeService = {
    getModels: async () => {
        try {
            const response = await api.get('/claude/models');
            return response.data;
        } catch (error) {
            console.error('Error fetching Claude models:', error);
            throw error;
        }
    },
};

// --- Chat Service for Persistent AI Chat History ---
const chatService = {
    // Chat Sessions
    listSessions: async () => {
        const response = await api.get('/chat-sessions');
        return response.data;
    },
    createSession: async (title = 'New Chat') => {
        const response = await api.post('/chat-sessions', { title });
        return response.data;
    },
    renameSession: async (sessionId, title) => {
        const response = await api.put(`/chat-sessions/${sessionId}`, { title });
        return response.data;
    },
    deleteSession: async (sessionId) => {
        const response = await api.delete(`/chat-sessions/${sessionId}`);
        return response.data;
    },
    // Chat Messages
    listMessages: async (sessionId) => {
        const response = await api.get(`/chat-sessions/${sessionId}/messages`);
        return response.data;
    },
    addMessage: async (sessionId, role, content, modelName = null) => {
        const response = await api.post(`/chat-sessions/${sessionId}/messages`, { 
            role, 
            content, 
            model_name: modelName 
        });
        return response.data;
    },
    updateMessage: async (sessionId, messageId, content) => {
        const response = await api.put(`/chat-sessions/${sessionId}/messages/${messageId}`, { role: 'user', content });
        return response.data;
    },
    deleteMessagesAfter: async (sessionId, afterTimestamp) => {
        const response = await api.delete(`/chat-sessions/${sessionId}/messages/after`, {
            params: { after_timestamp: afterTimestamp }
        });
        return response.data;
    },
};

export const troubleshootingService = {
    getConfig: async () => {
        const response = await api.get('/troubleshooting/config');
        return response.data;
    }
};

// --- Syslog Service ---
const syslogService = {
    // Send audit report to syslog server
    sendSyslog: async (auditId, syslogConfig) => {
        const response = await api.post('/syslog/send', {
            audit_id: auditId,
            syslog_server_ip: syslogConfig.serverIp,
            syslog_server_port: syslogConfig.serverPort,
            protocol: syslogConfig.protocol
        });
        return response.data;
    },

    // Get alert severity summary counts (excludes test logs)
    getSummary: async () => {
        const response = await api.get('/syslog/summary');
        return response.data;
    },

    // Get syslog history with pagination and filters
    getSyslogHistory: async (page = 1, pageSize = 20, filters = {}) => {
        const params = {
            page,
            page_size: pageSize,
            ...(filters.startDate && { start_date: filters.startDate }),
            ...(filters.endDate && { end_date: filters.endDate }),
            ...(filters.status && { status: filters.status }),
            ...(filters.protocol && { protocol: filters.protocol }),
            ...(filters.firewallId && { firewall_id: filters.firewallId }),
            ...(filters.firewallIp && { firewall_ip: filters.firewallIp }),
            ...(filters.alertType && { alert_type: filters.alertType }),
            ...(filters.auditScoreMin !== undefined && filters.auditScoreMin !== '' && { min_score: filters.auditScoreMin }),
            ...(filters.auditScoreMax !== undefined && filters.auditScoreMax !== '' && { max_score: filters.auditScoreMax })
        };
        const response = await api.get('/syslog/history', { params });
        return response.data;
    },

    // Get specific syslog entry
    getSyslogEntry: async (logId) => {
        const response = await api.get(`/syslog/history/${logId}`);
        return response.data;
    },

    // Resend failed syslog message
    resendSyslog: async (logId) => {
        const response = await api.post(`/syslog/resend/${logId}`);
        return response.data;
    },

    // Delete a single syslog entry
    deleteSyslog: async (logId) => {
        const response = await api.delete(`/syslog/history/${logId}`);
        return response.data;
    },

    // Bulk delete syslog entries
    bulkDeleteSyslogs: async (ids) => {
        const response = await api.post('/syslog/history/bulk-delete', { ids });
        return response.data;
    },

    // Get user's syslog configuration
    getSyslogConfig: async () => {
        const response = await api.get('/syslog/config');
        return response.data;
    },

    // Save user's syslog configuration
    saveSyslogConfig: async (config) => {
        const response = await api.put('/syslog/config', {
            syslog_server_ip: config.serverIp,
            syslog_server_port: config.serverPort,
            protocol: config.protocol
        });
        return response.data;
    },

    // Send test log with current system timestamp
    sendTestLog: async (syslogConfig) => {
        // Debug: Log what we're sending
        console.log('API: Sending test log with protocol:', syslogConfig.protocol);
        const requestData = {
            syslog_server_ip: syslogConfig.serverIp,
            syslog_server_port: syslogConfig.serverPort,
            protocol: syslogConfig.protocol
        };
        console.log('API: Request data:', requestData);
        const response = await api.post('/syslog/test', requestData);
        return response.data;
    }
};

// --- SOAR Service ---
const soarService = {
    // Send audit report to SOAR webhook
    sendToSOAR: async (auditId, soarConfig) => {
        const response = await api.post('/soar/send', {
            audit_id: auditId,
            webhook_url: soarConfig.webhookUrl,
            protocol: soarConfig.protocol,
            method: soarConfig.method,
            timeout: soarConfig.timeout,
            retry_attempts: soarConfig.retryAttempts,
            verify_ssl: soarConfig.verifySsl
        });
        return response.data;
    },

    // Get SOAR history with pagination and filters
    getSOARHistory: async (page = 1, pageSize = 20, filters = {}) => {
        const params = {
            page,
            page_size: pageSize,
            ...(filters.startDate && { start_date: filters.startDate }),
            ...(filters.endDate && { end_date: filters.endDate }),
            ...(filters.status && { status: filters.status }),
            ...(filters.alertType && { alert_type: filters.alertType })
        };
        const response = await api.get('/soar/history', { params });
        return response.data;
    },

    // Get specific SOAR entry
    getSOAREntry: async (logId) => {
        const response = await api.get(`/soar/history/${logId}`);
        return response.data;
    },

    // Retry failed SOAR webhook
    retrySOAR: async (logId) => {
        const response = await api.post(`/soar/retry/${logId}`);
        return response.data;
    },

    // Test SOAR webhook configuration
    testSOARWebhook: async (soarConfig) => {
        const response = await api.post('/soar/test', {
            webhook_url: soarConfig.webhookUrl,
            protocol: soarConfig.protocol,
            method: soarConfig.method,
            timeout: soarConfig.timeout,
            verify_ssl: soarConfig.verifySsl
        });
        return response.data;
    },

    // Get user's SOAR configuration
    getSOARConfig: async () => {
        const response = await api.get('/soar/config');
        return response.data;
    },

    // Save user's SOAR configuration
    saveSOARConfig: async (config) => {
        const response = await api.put('/soar/config', {
            webhook_url: config.webhookUrl,
            protocol: config.protocol,
            method: config.method,
            timeout: config.timeout,
            retry_attempts: config.retryAttempts,
            verify_ssl: config.verifySsl
        });
        return response.data;
    },

    // Delete a single SOAR entry
    deleteSOAR: async (logId) => {
        const response = await api.delete(`/soar/history/${logId}`);
        return response.data;
    },

    // Bulk delete SOAR entries
    bulkDeleteSOARs: async (ids) => {
        const response = await api.post('/soar/history/bulk-delete', { ids });
        return response.data;
    }
};

const llmService = {
    getStatus: async () => {
        const response = await api.get('/llm-status');
        return response.data;
    },
};

const backupService = {
    getInfo: async () => {
        const response = await api.get('/backup/info');
        return response.data;
    },

    exportDatabase: async () => {
        const response = await api.get('/backup/export', { responseType: 'blob' });
        return response;
    },

    importDatabase: async (file, onUploadProgress) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/backup/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
            onUploadProgress,
        });
        return response.data;
    },

    listSnapshots: async () => {
        const response = await api.get('/backup/snapshots');
        return response.data;
    },

    revertToSnapshot: async (filename) => {
        const response = await api.post(`/backup/revert/${encodeURIComponent(filename)}`);
        return response.data;
    },
};

const researchPaperService = {
    compileLatex: async (paperId, latex) => {
        const response = await api.post(`/research-paper/${paperId}/compile-latex`, { latex }, {
            responseType: 'blob',
            timeout: 90000,
        });
        return response.data;
    },
    uploadFile: async (paperId, file, subfolder = '') => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('subfolder', subfolder);
        const response = await api.post(`/research-paper/${paperId}/files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    listFiles: async (paperId) => {
        const response = await api.get(`/research-paper/${paperId}/files`);
        return response.data;
    },
    deleteFile: async (paperId, filePath) => {
        const response = await api.delete(`/research-paper/${paperId}/files/${filePath}`);
        return response.data;
    },
    moveFile: async (paperId, filePath, targetSubfolder) => {
        const response = await api.post(
            `/research-paper/${paperId}/files/${filePath}/move`,
            { target_subfolder: targetSubfolder || '' },
        );
        return response.data;
    },
    renameFile: async (paperId, filePath, newName) => {
        const response = await api.post(
            `/research-paper/${paperId}/files/${filePath}/rename`,
            { new_name: newName },
        );
        return response.data;
    },
    getLatex: async (paperId) => {
        const response = await api.get(`/research-paper/${paperId}/latex`);
        return response.data;
    },
    saveLatex: async (paperId, latex) => {
        const response = await api.put(`/research-paper/${paperId}/latex`, { latex });
        return response.data;
    },
    revertLatex: async (paperId) => {
        const response = await api.post(`/research-paper/${paperId}/revert-last`);
        return response.data;
    },
    getCompiledPdf: async (paperId, { cacheBust = false } = {}) => {
        const response = await api.get(`/research-paper/${paperId}/compiled-pdf`, {
            responseType: 'blob',
            timeout: 30000,
            params: cacheBust ? { _: Date.now() } : undefined,
        });
        return {
            blob: response.data,
            compiledAt: response.headers['x-compiled-at'],
        };
    },
    downloadZip: async (paperId) => {
        const response = await api.get(`/research-paper/${paperId}/download-zip`, {
            responseType: 'blob',
            timeout: 30000,
        });
        return response.data;
    },
    // Auto-generated standalone Highlights PDF, freshly compiled from the
    // current paper.tex. Used for the separate "highlights" upload that
    // Elsevier asks for at Editorial Manager submission.
    downloadHighlightsPdf: async (paperId) => {
        const response = await api.get(`/research-paper/${paperId}/highlights-pdf`, {
            responseType: 'blob',
            timeout: 60000,
        });
        return response.data;
    },
    downloadFile: async (paperId, filePath) => {
        const response = await api.get(`/research-paper/${paperId}/files/${filePath}/download`, {
            responseType: 'blob',
            timeout: 30000,
        });
        return response.data;
    },
    // Download an entire subfolder (e.g. 'figures') as a zip archive.
    downloadFolder: async (paperId, folderPath) => {
        const response = await api.get(`/research-paper/${paperId}/download-folder/${folderPath}`, {
            responseType: 'blob',
            timeout: 60000,
        });
        return response.data;
    },
};

export { firewallService, ollamaService, mlxService, openaiService, googleAiStudioService, openRouterService, groqService, claudeService, userService, chatService, syslogService, soarService, llmService, backupService, researchPaperService };
export default api;
